import { type ReactNode, useLayoutEffect, useMemo, useRef } from 'react';
import type {
  PageDtoResponse,
  TableColumnConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import { ErrorMessage, LoadingIndicator } from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';
import deepEqual from 'deep-equal';

import { fetchPage, type PageQueryKey } from '../../api/portalGateway/page';
import {
  useCustomTableFilterQuery,
  usePageFilterQuery,
} from '../../hooks/filter';
import { useGlobalFilterLoaded } from '../../hooks/filter/useGlobalFilterQuery';
import { useStore } from '../../store';
import { getCustomPageFilterInstanceId } from '../../store/hooks/useCombinedPageFilterSelections';
import { useEffectiveGlobalSelections } from '../../store/hooks/useEffectiveGlobalSelections';
import { useSelections } from '../../store/hooks/useSelections';
import { useDashboardEditModeStore } from '../../store/states/dashboardEditMode';
import type { FilterSelections } from '../../store/states/filterSelection';
import { prependRoleId } from '../../utils/prependRoleId';
import { CustomTableFilters } from '../filter/customTableFilter/CustomTableFilters';
import { PageFilters } from '../filter/PageFilters';

import { DashboardFilterPills } from './DashboardFilterPills';
import {
  buildDashboardFilterPills,
  type DashboardFilterSelections,
  storedFilterSelections,
} from './dashboardTileFilters';

type CustomFilterColumn = Pick<TableColumnConfigDto, 'id' | 'customFilter'>;

type UseDashboardTileFiltersParams = {
  enabled: boolean;
  editable: boolean;
  contentSourcePageId?: string;
  tileInstanceId: string;
  savedPageFilterSelections?: DashboardFilterSelections;
  savedCustomTableFilterSelections?: DashboardFilterSelections;
  pageFilterInstanceId: string;
  tableFilterInstanceId: string;
  widgetId: string;
  activeCustomFilterIds: readonly string[];
  customFilterColumns: readonly CustomFilterColumn[];
  /** True while the tile's active custom-filter list is still loading. */
  isFilterCustomizationLoading?: boolean;
};

const EMPTY_SELECTIONS: DashboardFilterSelections = {};

function visibleFilterSelections(
  selections: FilterSelections,
  visibleFilters: readonly { key: string }[] | null
): FilterSelections {
  if (visibleFilters == null) {
    return selections;
  }
  const visibleKeys = new Set(visibleFilters.map((filter) => filter.key));
  return Object.fromEntries(
    Object.entries(selections).filter(([key]) => visibleKeys.has(key))
  );
}

function replaceSelections(entityId: string, selections: FilterSelections) {
  const state = useStore.getState();
  const current = state.filterSelections[entityId] ?? {};
  const keysToRemove = Object.keys(current).filter(
    (key) => !(key in selections)
  );
  state.batchPresetFilterSelections(entityId, selections, keysToRemove);
  state.setFilterInitialized(entityId);
}

export function useDashboardTileFilters({
  enabled,
  editable,
  contentSourcePageId,
  tileInstanceId,
  savedPageFilterSelections,
  savedCustomTableFilterSelections,
  pageFilterInstanceId,
  tableFilterInstanceId,
  widgetId,
  activeCustomFilterIds,
  customFilterColumns,
  isFilterCustomizationLoading = false,
}: UseDashboardTileFiltersParams): {
  ready: boolean;
  pageFilterSelections: DashboardFilterSelections;
  customTableFilterSelections: DashboardFilterSelections;
  filterBar: ReactNode;
} {
  const updateTileFilterSelections = useDashboardEditModeStore(
    (state) => state.updateTileFilterSelections
  );
  const adjustTileFiltersPending = useDashboardEditModeStore(
    (state) => state.adjustTileFiltersPending
  );
  const isSaving = useDashboardEditModeStore((state) => state.isSaving);
  // Freeze the interactive filter bar while save is in flight so clear/add and
  // selection edits cannot race the payload already being persisted.
  const filtersEditable = editable && !isSaving;
  const globalSelections = useEffectiveGlobalSelections();
  const globalFilterLoaded = useGlobalFilterLoaded();
  const pageSelections = useSelections(pageFilterInstanceId);
  const customTableSelections = useSelections(tableFilterInstanceId);

  const pageQuery = useQuery({
    queryKey: prependRoleId<PageQueryKey>([
      'page',
      contentSourcePageId ?? 'unknown',
    ]),
    queryFn: fetchPage,
    enabled: enabled && contentSourcePageId != null,
  });
  const page: PageDtoResponse | undefined = pageQuery.data;

  const visibleFilters = useMemo(
    () =>
      (page?.data.pageFilter?.filters ?? []).filter(
        (filter) => filter.hidden !== true
      ),
    [page]
  );

  // Active columns always load. Inactive columns stay relevant only while a
  // selection still exists: live store during edit (so clearing a column drops
  // the option query and cannot first-value sanitize it back), saved props in
  // view mode so pills/options work before the store is seeded and for viewers
  // whose customization has not activated those columns.
  const relevantCustomFilters = useMemo(
    () =>
      customFilterColumns.flatMap((column) => {
        if (column.customFilter == null) {
          return [];
        }
        const filterKey = column.customFilter.key;
        const isActive = activeCustomFilterIds.includes(column.id);
        const hasLiveSelection = customTableSelections?.[filterKey] != null;
        const hasSavedSelectionForView =
          !editable && savedCustomTableFilterSelections?.[filterKey] != null;
        return isActive || hasLiveSelection || hasSavedSelectionForView
          ? [column.customFilter]
          : [];
      }),
    [
      activeCustomFilterIds,
      customFilterColumns,
      customTableSelections,
      editable,
      savedCustomTableFilterSelections,
    ]
  );

  const wasEditable = useRef(editable);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    const leftEditMode = wasEditable.current && !editable;
    wasEditable.current = editable;
    const initialized =
      useStore.getState().filterInitialized[pageFilterInstanceId] === true;
    if (initialized && !leftEditMode) {
      return;
    }
    replaceSelections(pageFilterInstanceId, savedPageFilterSelections ?? {});
    replaceSelections(
      tableFilterInstanceId,
      savedCustomTableFilterSelections ?? {}
    );
  }, [
    editable,
    enabled,
    pageFilterInstanceId,
    savedPageFilterSelections,
    savedCustomTableFilterSelections,
    tableFilterInstanceId,
  ]);

  useLayoutEffect(() => {
    if (!enabled || page == null) {
      return;
    }
    const hiddenKeys = (page.data.pageFilter?.filters ?? [])
      .filter((filter) => filter.hidden === true)
      .map((filter) => filter.key);
    if (hiddenKeys.length === 0) {
      return;
    }
    const state = useStore.getState();
    const current = state.filterSelections[pageFilterInstanceId] ?? {};
    const keysToRemove = hiddenKeys.filter((key) => key in current);
    if (keysToRemove.length === 0) {
      return;
    }
    state.batchPresetFilterSelections(pageFilterInstanceId, {}, keysToRemove);
  }, [enabled, page, pageFilterInstanceId]);

  const pageFilterQuery = usePageFilterQuery(
    contentSourcePageId ?? '',
    pageFilterInstanceId,
    enabled && page != null ? visibleFilters : undefined
  );
  const customTableFilterQuery = useCustomTableFilterQuery(
    widgetId,
    tableFilterInstanceId,
    pageFilterInstanceId,
    enabled ? relevantCustomFilters : []
  );

  useLayoutEffect(() => {
    if (!enabled || (contentSourcePageId != null && !pageQuery.isError)) {
      return;
    }
    // No source page, or the source page failed: there is no page-filter
    // query. Mark it loaded so custom table filters are not blocked on it.
    useStore.getState().setFilterQueryLoaded(pageFilterInstanceId);
  }, [contentSourcePageId, enabled, pageFilterInstanceId, pageQuery.isError]);

  const customPageFilterInstanceId =
    getCustomPageFilterInstanceId(pageFilterInstanceId);
  const customPageFilterLoaded = useStore(
    (state) => state.filterQueryLoaded[customPageFilterInstanceId] === true
  );
  useLayoutEffect(() => {
    if (!enabled || customPageFilterLoaded) {
      return;
    }
    // Dashboard tiles do not load source-page custom page filters. Mark that
    // query settled so custom table filters are not blocked on it.
    // Changing a page-filter selection clears this flag; set it again or the
    // table stays on its loading overlay.
    useStore.getState().setFilterQueryLoaded(customPageFilterInstanceId);
  }, [customPageFilterInstanceId, customPageFilterLoaded, enabled]);

  const visiblePageSelections = useMemo(
    () =>
      visibleFilterSelections(
        pageSelections,
        page == null ? null : visibleFilters
      ),
    [page, pageSelections, visibleFilters]
  );

  // Same rule as a normal table: data waits until each filter query that
  // exists reports success. An empty list is already success. A new
  // selection drops success until that request succeeds again.
  // A missing or failed source page has no page-filter query. Custom
  // filters still have to succeed on their own, and an in-flight
  // customization query is not an empty active-filter list.
  const pageFiltersReady =
    contentSourcePageId == null ||
    pageQuery.isError ||
    (pageQuery.isSuccess && pageFilterQuery.isSuccess);
  const ready =
    !enabled ||
    (globalFilterLoaded &&
      pageFiltersReady &&
      !isFilterCustomizationLoading &&
      customTableFilterQuery.isSuccess);

  const sourcePageLoading =
    contentSourcePageId != null && !pageQuery.isSuccess && !pageQuery.isError;
  const pageFilterFailed = pageFilterQuery.isError;
  // Custom table filters stay disabled after a page-filter failure. A source
  // page failure is different: there is no page-filter query, so custom
  // filters can still load.
  const customFiltersBlocked = pageFilterFailed;
  const pageFiltersLoading =
    visibleFilters.length > 0 &&
    !pageFilterQuery.isSuccess &&
    !pageFilterFailed;
  const customFiltersLoading =
    isFilterCustomizationLoading ||
    (relevantCustomFilters.length > 0 &&
      !customFiltersBlocked &&
      !customTableFilterQuery.isSuccess &&
      !customTableFilterQuery.isError);
  const filtersLoading =
    sourcePageLoading || pageFiltersLoading || customFiltersLoading;

  const snapshot = useRef<ReturnType<typeof storedFilterSelections> | null>(
    null
  );
  useLayoutEffect(() => {
    if (!enabled || !filtersEditable) {
      snapshot.current = null;
      return;
    }
    if (filtersLoading) {
      return;
    }
    // Read the store after page-filter sanitizing has written first-value
    // and dropped invalid values in an earlier layout effect. The first
    // resolved selection is the baseline, so opening edit does not count
    // as a change.
    const state = useStore.getState();
    const currentVisiblePageSelections = visibleFilterSelections(
      state.filterSelections[pageFilterInstanceId] ?? {},
      page == null ? null : visibleFilters
    );
    const nextSelections = storedFilterSelections({
      pageFilterSelections: currentVisiblePageSelections,
      customTableFilterSelections:
        state.filterSelections[tableFilterInstanceId] ?? {},
    });
    if (snapshot.current == null) {
      snapshot.current = nextSelections;
      return;
    }
    if (deepEqual(snapshot.current, nextSelections)) {
      return;
    }
    snapshot.current = nextSelections;
    updateTileFilterSelections(tileInstanceId, nextSelections);
  }, [
    customTableSelections,
    enabled,
    filtersEditable,
    filtersLoading,
    page,
    pageFilterInstanceId,
    tableFilterInstanceId,
    tileInstanceId,
    updateTileFilterSelections,
    visibleFilters,
    visiblePageSelections,
  ]);

  useLayoutEffect(() => {
    if (!enabled || !filtersEditable || !filtersLoading) {
      return;
    }
    adjustTileFiltersPending(tileInstanceId, 1);
    return () => adjustTileFiltersPending(tileInstanceId, -1);
  }, [
    adjustTileFiltersPending,
    enabled,
    filtersEditable,
    filtersLoading,
    tileInstanceId,
  ]);

  const filterBar = useMemo(() => {
    if (!enabled) {
      return null;
    }
    const pageFilterError = pageFilterQuery.isError
      ? pageFilterQuery.error
      : null;
    const customFilterError = customTableFilterQuery.isError
      ? customTableFilterQuery.error
      : null;
    // Edit mode uses the same wait. The first settled selection is the dirty
    // baseline, so a change made while options are loading would be omitted
    // from the save payload.
    if (filtersLoading) {
      return <LoadingIndicator data-testid="dashboard-filter-loading" />;
    }

    if (filtersEditable) {
      const pageFilterConfig =
        contentSourcePageId != null &&
        page?.data.pageFilter != null &&
        visibleFilters.length > 0
          ? { ...page.data.pageFilter, filters: visibleFilters }
          : null;
      if (pageFilterConfig == null && activeCustomFilterIds.length === 0) {
        return null;
      }
      return (
        <>
          {customFilterError != null && (
            <ErrorMessage>{customFilterError.message}</ErrorMessage>
          )}
          {pageFilterConfig != null && contentSourcePageId != null ? (
            <PageFilters
              config={pageFilterConfig}
              embedded
              filterKey={pageFilterInstanceId}
              pageId={contentSourcePageId}
              showCustomPageFilters={false}
            />
          ) : null}
          {activeCustomFilterIds.length > 0 ? (
            <CustomTableFilters
              activeCustomFilterIds={[...activeCustomFilterIds]}
              columnConfigs={[...customFilterColumns]}
              pageFilterInstanceId={pageFilterInstanceId}
              tableFilterInstanceId={tableFilterInstanceId}
              widgetId={widgetId}
            />
          ) : null}
        </>
      );
    }

    const pagePills =
      pageFilterError != null
        ? []
        : buildDashboardFilterPills(
            visiblePageSelections,
            visibleFilters,
            pageFilterQuery.validatedData
          );
    const customPills =
      customFiltersBlocked || customFilterError != null
        ? []
        : buildDashboardFilterPills(
            customTableSelections,
            relevantCustomFilters,
            customTableFilterQuery.validatedData
          );
    const pills = [...pagePills, ...customPills];
    if (
      pageFilterError == null &&
      customFilterError == null &&
      pills.length === 0
    ) {
      return null;
    }
    return (
      <>
        {pageFilterError != null && (
          <ErrorMessage>{pageFilterError.message}</ErrorMessage>
        )}
        {customFilterError != null && (
          <ErrorMessage>{customFilterError.message}</ErrorMessage>
        )}
        {pills.length > 0 ? <DashboardFilterPills pills={pills} /> : null}
      </>
    );
  }, [
    activeCustomFilterIds,
    contentSourcePageId,
    customFilterColumns,
    customFiltersBlocked,
    customTableFilterQuery.error,
    customTableFilterQuery.isError,
    customTableFilterQuery.validatedData,
    customTableSelections,
    enabled,
    filtersEditable,
    filtersLoading,
    page,
    pageFilterInstanceId,
    pageFilterQuery.error,
    pageFilterQuery.isError,
    pageFilterQuery.validatedData,
    relevantCustomFilters,
    tableFilterInstanceId,
    visibleFilters,
    visiblePageSelections,
    widgetId,
  ]);

  return {
    ready,
    pageFilterSelections: enabled
      ? { ...globalSelections, ...visiblePageSelections }
      : EMPTY_SELECTIONS,
    customTableFilterSelections: enabled
      ? customTableSelections
      : EMPTY_SELECTIONS,
    filterBar,
  };
}
